import type { ConsoleApi, ConsoleConnection, ConsoleStoreSummary } from './console-api.js';
import {
  draftVersion,
  emptyDraft,
  parseDraft,
  type OnboardingDraft,
} from './onboarding-state.js';

export interface LoadedOnboarding {
  draft: OnboardingDraft;
  connection: ConsoleConnection | null;
  store: ConsoleStoreSummary | null;
}

/**
 * The console's persistence seam. Everything above this file works with a draft in memory;
 * everything below it is how that draft reaches the server. If persistence ever moves again,
 * this is the file that changes.
 */
export interface OnboardingStore {
  load(): Promise<LoadedOnboarding>;
  /** Records the draft and schedules a write. Returns immedaytely; typing must never block. */
  scheduleSave(draft: OnboardingDraft): void;
  /** Writes any pending draft now. Called on navigation and when the tab goes away. */
  flush(): Promise<void>;
  /** Optional hook for stores that can surface asynchronous persistence failures. */
  subscribeToErrors?(onError: (error: unknown) => void): () => void;
  dispose(): void;
}

export interface CreateHttpOnboardingStoreInput {
  api: ConsoleApi;
  userId: string;
  debounceMs?: number;
  onError?: (error: unknown) => void;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

export function createHttpOnboardingStore(
  input: CreateHttpOnboardingStoreInput,
): OnboardingStore {
  const debounceMs = input.debounceMs ?? 800;
  const localBackup = createLocalDraftBackup(input.userId, input.storage ?? globalThis.localStorage);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: OnboardingDraft | null = null;
  let inFlight: Promise<void> = Promise.resolve();
  let errorListener: ((error: unknown) => void) | null = null;

  function clearTimer(): void {
    if (timer === null) {
      return;
    }

    clearTimeout(timer);
    timer = null;
  }

  async function write(draft: OnboardingDraft): Promise<void> {
    try {
      await input.api.putOnboarding(operationalDraft(draft));
      localBackup.clear();
    } catch (error) {
      localBackup.save(draft);
      input.onError?.(error);
      errorListener?.(error);
    }
  }

  function writePending(): void {
    const draft = pending;
    pending = null;

    if (!draft) {
      return;
    }

    inFlight = inFlight.then(() => write(draft));
  }

  return {
    async load() {
      const payload = await input.api.getOnboarding();
      const remoteDraft = parseDraft(payload.draft);
      const backupDraft = localBackup.load();

      return {
        // A draft the current schema cannot read degrades to an empty one rather than blanking
        // the console with an error. A local backup is only present while a remote write is still
        // pending or failed, so it preserves the merchant's current work without restoring old
        // server-saved answers into a fresh session.
        draft: backupDraft ?? remoteDraft ?? emptyDraft(),
        connection: payload.connection,
        store: payload.store,
      };
    },

    scheduleSave(draft) {
      localBackup.save(draft);
      pending = draft;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        writePending();
      }, debounceMs);
    },

    async flush() {
      clearTimer();
      writePending();

      await inFlight;
    },

    subscribeToErrors(onError) {
      errorListener = onError;

      return () => {
        if (errorListener === onError) {
          errorListener = null;
        }
      };
    },

    dispose() {
      clearTimer();
      pending = null;
    },
  };
}

function operationalDraft(draft: OnboardingDraft): OnboardingDraft {
  const empty = emptyDraft();

  return {
    ...empty,
    version: draftVersion,
    updatedAt: draft.updatedAt,
    attribution: draft.attribution,

    store: {
      ...empty.store,
      name: draft.store.name,
      googlePlaceId: draft.store.googlePlaceId,
      formattedAddress: draft.store.formattedAddress,
      latitude: draft.store.latitude,
      longitude: draft.store.longitude,
    },
    service: {
      ...empty.service,
      schedules: draft.service.schedules,
      paymentMethods: draft.service.paymentMethods,
      fulfillmentMethods: draft.service.fulfillmentMethods,
      deliveryNotes: draft.service.deliveryNotes,
      deliveryArea: draft.service.deliveryArea,
    },
  };
}

function createLocalDraftBackup(
  userId: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined,
) {
  const key = `marketside:onboarding-draft:v1:${encodeURIComponent(userId)}`;

  return {
    load(): OnboardingDraft | null {
      if (!storage) {
        return null;
      }

      try {
        const raw = storage.getItem(key);

        if (!raw) {
          return null;
        }

        return parseDraft(JSON.parse(raw));
      } catch {
        return null;
      }
    },

    save(draft: OnboardingDraft): void {
      if (!storage) {
        return;
      }

      try {
        storage.setItem(key, JSON.stringify(operationalDraft(draft)));
      } catch {
        // Local backup is best-effort. The in-memory draft still protects the current tab.
      }
    },

    clear(): void {
      if (!storage) {
        return;
      }

      try {
        storage.removeItem(key);
      } catch {
        // Best-effort cleanup only.
      }
    },
  };
}

/** Test double. Keeps the same debounce-and-flush contract without a network. */
export function createInMemoryOnboardingStore(
  initial: Partial<LoadedOnboarding> = {},
): OnboardingStore & { saved: OnboardingDraft[]; current: LoadedOnboarding } {
  const state: LoadedOnboarding = {
    draft: initial.draft ?? emptyDraft(),
    connection: initial.connection ?? null,
    store: initial.store ?? null,
  };
  const saved: OnboardingDraft[] = [];
  let pending: OnboardingDraft | null = null;

  return {
    saved,
    current: state,

    async load() {
      return { draft: state.draft, connection: state.connection, store: state.store };
    },

    scheduleSave(draft) {
      pending = draft;
    },

    async flush() {
      if (!pending) {
        return;
      }

      state.draft = pending;
      saved.push(pending);
      pending = null;
    },

    dispose() {
      pending = null;
    },
  };
}
